import styles from "./checklist-item.module.css";

import { useState, forwardRef, useCallback, useMemo, useEffect } from "react";

import { Flex, Input, Button, Dropdown } from "antd";

import PencilAltIcon from "@/icons/PencilAltIcon";
import DotsHorizontalIcon from "@/icons/DotsHorizontalIcon";
import MenuAlt4Icon from "@/icons/MenuAlt4Icon";
import DuplicateIcon from "@/icons/DuplicateIcon";
import TrashIcon from "@/icons/TrashIcon";

const ChecklistItem = forwardRef(
  (
    {
      item,
      isEditing = false,
      onSave,
      onDelete,
      onCancel,
      style,
      readOnly = false,
      dragListeners,
      dragAttributes,
      ...otherProps
    },
    ref,
  ) => {
    const [newTitle, setNewTitle] = useState(item.title);
    const [isEdit, setIsEdit] = useState(isEditing);

    // Синхронизация с внешним состоянием
    useEffect(() => {
      setIsEdit(isEditing);
    }, [isEditing]);

    // Синхронизация текста при изменении item
    useEffect(() => {
      if (!isEdit) {
        setNewTitle(item.title);
      }
    }, [item.title, isEdit]);

    const handleTextChange = useCallback((e) => {
      setNewTitle(e.target.value);
    }, []);

    const handleEdit = useCallback(() => {
      if (!readOnly) {
        setIsEdit(true);
      }
    }, [readOnly]);

    const handleSave = useCallback(() => {
      const trimmedTitle = newTitle.trim();
      if (trimmedTitle && trimmedTitle !== item.title) {
        onSave({
          title: trimmedTitle,
          uuid: item.uuid,
        });
      }
      setIsEdit(false);
    }, [newTitle, onSave, item.uuid, item.title]);

    const handleCancel = useCallback(() => {
      setNewTitle(item.title);
      setIsEdit(false);
      onCancel?.();
    }, [item.title, onCancel]);

    const handleDuplicate = useCallback(() => {
      onSave({ title: item.title });
    }, [item.title, onSave]);

    const handleDelete = useCallback(() => {
      onDelete(item.uuid);
    }, [item.uuid, onDelete]);

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Escape") {
          handleCancel();
        }
      },
      [handleCancel],
    );

    const dropdownMenuItems = useMemo(
      () => [
        {
          key: "edit",
          onClick: handleEdit,
          label: (
            <Flex gap={8} align="center" style={{ color: "#777" }}>
              <PencilAltIcon style={{ fontSize: 16, color: "#BBBBBB" }} />
              Редактировать
            </Flex>
          ),
        },
        {
          key: "duplicate",
          onClick: handleDuplicate,
          label: (
            <Flex gap={8} align="center" style={{ color: "#777" }}>
              <DuplicateIcon style={{ fontSize: 16, color: "#BBBBBB" }} />
              Дублировать
            </Flex>
          ),
        },
        {
          key: "delete",
          onClick: handleDelete,
          label: (
            <Flex gap={8} align="center" style={{ color: "#777" }}>
              <TrashIcon style={{ fontSize: 16, color: "#BBBBBB" }} />
              Удалить
            </Flex>
          ),
        },
      ],
      [handleEdit, handleDuplicate, handleDelete],
    );

    const isTitleChanged = newTitle.trim() !== item.title;
    const canSave = newTitle.trim() && isTitleChanged;

    const renderEditMode = () => (
      <>
        <Input
          placeholder="Введите критерий"
          value={newTitle}
          allowClear
          onChange={handleTextChange}
          onPressEnter={handleSave}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <Flex gap={6}>
          <Button onClick={handleCancel}>Отмена</Button>
          <Button type="primary" disabled={!canSave} onClick={handleSave}>
            Сохранить
          </Button>
        </Flex>
      </>
    );

    const showActions = !readOnly && !item.is_completed && !isEdit;

    const renderViewMode = () => (
      <>
        <div className={styles.text}>{item.title}</div>
        <Flex
          gap={6}
          style={{ minWidth: 70, flex: "0 0 70px", display: "block" }}
        >
          {showActions && (
            <>
              <Dropdown
                menu={{ items: dropdownMenuItems }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button
                  color="default"
                  variant="text"
                  type="text"
                  icon={<DotsHorizontalIcon />}
                  style={{ padding: 0 }}
                />
              </Dropdown>

              <Button
                color="default"
                variant="text"
                type="text"
                icon={<MenuAlt4Icon />}
                style={{ padding: 0, cursor: "grab", touchAction: "none" }}
                {...dragListeners}
                {...dragAttributes}
              />
            </>
          )}
        </Flex>
      </>
    );

    const containerClasses = [
      styles.wrap,
      isEdit && styles.editing,
      item.is_completed && styles.checked,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <Flex
        ref={ref}
        className={containerClasses}
        gap={16}
        align="flex-start"
        style={style}
        {...otherProps}
      >
        {isEdit ? renderEditMode() : renderViewMode()}
      </Flex>
    );
  },
);

export default ChecklistItem;
